/**
 * Tela de convite.
 *
 * Mostra o servidor antes de pedir login: quem recebe um link precisa saber
 * para onde está indo antes de criar conta.
 */

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowRight, Users } from 'lucide-react'
import { avatarColor, initials } from '@nexus/shared'
import { api, ApiError } from '@/lib/api'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { Button, EmptyState, Input, Spinner } from '@/components/ui'

interface Preview {
  code: string
  guild: { id: string; name: string; iconUrl: string | null; description: string | null; memberCount: number }
  expiresAt: string | null
}

export function InviteView() {
  const params = useParams<{ code?: string }>()
  const navigate = useNavigate()

  const authStatus = useAuthStore((s) => s.status)
  const applyEvent = useAppStore((s) => s.applyEvent)
  const selectGuild = useAppStore((s) => s.selectGuild)

  const [code, setCode] = useState(params.code ?? '')
  const [preview, setPreview] = useState<Preview | null>(null)
  const [loading, setLoading] = useState(Boolean(params.code))
  const [joining, setJoining] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!params.code) {
      setLoading(false)
      return
    }

    void api.invites
      .preview(params.code)
      .then((result) => setPreview(result as Preview))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Convite inválido'))
      .finally(() => setLoading(false))
  }, [params.code])

  const handleLookup = async () => {
    const trimmed = extractCode(code)
    if (!trimmed) return

    setLoading(true)
    setError(null)
    try {
      setPreview((await api.invites.preview(trimmed)) as Preview)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Convite inválido')
      setPreview(null)
    } finally {
      setLoading(false)
    }
  }

  const handleJoin = async () => {
    if (!preview) return

    if (authStatus !== 'authenticated') {
      navigate(`/cadastro?convite=${preview.code}`)
      return
    }

    setJoining(true)
    setError(null)
    try {
      await api.invites.accept(preview.code)
      const payload = await api.guilds.detail(preview.guild.id)
      applyEvent({ op: 'guild_create', d: payload, s: 0 })
      selectGuild(payload.guild.id)
      navigate('/', { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível entrar')
    } finally {
      setJoining(false)
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-surface-base p-6">
      <div className="w-full max-w-md rounded-xl bg-surface-raised p-6">
        {loading ? (
          <div className="flex justify-center py-10">
            <Spinner className="size-8 text-brand" />
          </div>
        ) : preview ? (
          <div className="text-center">
            <div
              className="mx-auto flex size-20 items-center justify-center overflow-hidden rounded-2xl text-2xl font-bold text-white"
              style={preview.guild.iconUrl ? undefined : { backgroundColor: avatarColor(preview.guild.id) }}
            >
              {preview.guild.iconUrl ? (
                <img src={preview.guild.iconUrl} alt="" className="size-full object-cover" />
              ) : (
                initials(preview.guild.name)
              )}
            </div>

            <p className="mt-4 text-sm text-content-secondary">Você foi convidado para</p>
            <h1 className="mt-1 text-2xl font-bold text-content-primary">{preview.guild.name}</h1>

            {preview.guild.description ? (
              <p className="mt-2 text-sm leading-relaxed text-content-secondary">
                {preview.guild.description}
              </p>
            ) : null}

            <p className="mt-3 flex items-center justify-center gap-1.5 text-sm text-content-muted">
              <Users className="size-4" />
              {preview.guild.memberCount}{' '}
              {preview.guild.memberCount === 1 ? 'membro' : 'membros'}
            </p>

            {error ? <p className="mt-3 text-sm text-danger">{error}</p> : null}

            <Button size="lg" full className="mt-6" loading={joining} onClick={() => void handleJoin()}>
              {authStatus === 'authenticated' ? 'Entrar no servidor' : 'Criar conta e entrar'}
              <ArrowRight className="size-4" />
            </Button>

            <button
              type="button"
              onClick={() => navigate('/')}
              className="mt-3 text-sm text-content-muted transition-colors hover:text-content-secondary"
            >
              Voltar
            </button>
          </div>
        ) : (
          <>
            <EmptyState
              icon={<Users />}
              title="Entrar em um servidor"
              description="Cole o link ou o código do convite que você recebeu."
            />

            <div className="mt-2 flex gap-2">
              <Input
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void handleLookup()}
                placeholder="https://exemplo.com/entrar/abc123"
              />
              <Button onClick={() => void handleLookup()} disabled={!code.trim()}>
                Buscar
              </Button>
            </div>

            {error ? <p className="mt-3 text-center text-sm text-danger">{error}</p> : null}

            <button
              type="button"
              onClick={() => navigate('/')}
              className="mt-4 w-full text-center text-sm text-content-muted transition-colors hover:text-content-secondary"
            >
              Voltar
            </button>
          </>
        )}
      </div>
    </div>
  )
}

/** Aceita tanto o código puro quanto a URL completa colada. */
function extractCode(input: string): string {
  const trimmed = input.trim()
  const match = trimmed.match(/(?:entrar\/)?([a-z0-9]{6,16})\/?$/i)
  return match?.[1] ?? trimmed
}
